import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, IconButton, Tooltip, CircularProgress, Snackbar, Alert, FormControlLabel, Checkbox, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { pdfjs } from 'react-pdf';
import axios from 'axios';
import { API, getAuthHeader } from './customerApp/shared';

const fmtEmailTime = (d) => (d ? new Date(d).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

/**
 * Contract templates: one PDF, with the spots a new hire must sign, initial, date
 * or type placed on its pages. Positions are stored as fractions of the page so
 * they land in the same place on the signing page and in the signed PDF.
 */
// Two sides: the contractor fills theirs while signing; the company's are yours — blanks are
// asked for when you send, and signature/name/date are drawn when you sign off afterwards.
const APPLICANT_FIELDS = [
  { type: 'signature', label: 'Signature', color: '#1976d2', w: 0.28, h: 0.045 },
  { type: 'initials', label: 'Initials', color: '#7b1fa2', w: 0.09, h: 0.035 },
  { type: 'date', label: 'Date', color: '#2e7d32', w: 0.14, h: 0.03 },
  { type: 'name', label: 'Printed name', color: '#ef6c00', w: 0.26, h: 0.03 },
  { type: 'address1', label: 'Address line 1', color: '#00838f', w: 0.4, h: 0.03 },
  { type: 'address2', label: 'City, state, ZIP', color: '#00838f', w: 0.4, h: 0.03 },
  { type: 'phone', label: 'Phone number', color: '#5d4037', w: 0.22, h: 0.03 },
  { type: 'email', label: 'Email address', color: '#5d4037', w: 0.32, h: 0.03 },
  { type: 'text', label: 'Text box (they type)', color: '#616161', w: 0.26, h: 0.03 }
];
const COMPANY_FIELDS = [
  { type: 'signature', label: 'Your signature', color: '#c62828', w: 0.28, h: 0.045 },
  { type: 'name', label: 'Your printed name', color: '#ad1457', w: 0.26, h: 0.03 },
  { type: 'date', label: 'Date you sign', color: '#6a1b9a', w: 0.14, h: 0.03 },
  { type: 'text', label: 'Blank you fill when sending', color: '#37474f', w: 0.26, h: 0.03 }
];
const ALL_FIELDS = [...APPLICANT_FIELDS.map((f) => ({ ...f, party: 'applicant' })), ...COMPANY_FIELDS.map((f) => ({ ...f, party: 'company' }))];
const TYPED = ['text', 'address1', 'address2', 'phone', 'email'];
const typeInfo = (f) => ALL_FIELDS.find((x) => x.type === f.type && x.party === (f.party || 'applicant')) || APPLICANT_FIELDS[8];
const partyLabel = (f) => ((f.party || 'applicant') === 'company' ? 'You' : 'Contractor');
const newId = () => Math.random().toString(16).slice(2, 12);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Renders every page of the PDF to a canvas; overlays live in the parent. */
function PdfPage({ pdf, pageNumber, width, onSize, children, onPointerDown }) {
  const canvasRef = useRef(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page = await pdf.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const scale = width / base.width;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(viewport.width * ratio);
      canvas.height = Math.round(viewport.height * ratio);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      await page.render({ canvasContext: ctx, viewport }).promise;
      if (!cancelled) { setHeight(viewport.height); onSize?.(pageNumber, { width: viewport.width, height: viewport.height }); }
    })();
    return () => { cancelled = true; };
  }, [pdf, pageNumber, width, onSize]);
  return (
    <Box sx={{ position: 'relative', width, height: height || undefined, mb: 2, boxShadow: 2, bgcolor: '#fff' }} onPointerDown={onPointerDown}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      {children}
    </Box>
  );
}

/** The designer: drag fields around the rendered pages, resize from the corner, edit the label. */
function TemplateEditor({ template, onClose, onSaved, notify }) {
  const [title, setTitle] = useState(template.title);
  const [fields, setFields] = useState(template.fields || []);
  const [pdf, setPdf] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const sizes = useRef({});
  const drag = useRef(null);
  const pageWidth = Math.min(820, typeof window !== 'undefined' ? window.innerWidth - 360 : 820);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/careers-admin/contract-templates/${template.id}/file`, { headers: getAuthHeader(), responseType: 'arraybuffer' });
        const doc = await pdfjs.getDocument({ data: res.data }).promise;
        if (!cancelled) setPdf(doc);
      } catch (err) {
        if (!cancelled) setLoadError(err.response?.data?.message || err.message || 'Could not open the PDF');
      }
    })();
    return () => { cancelled = true; };
  }, [template.id]);

  const onSize = useCallback((n, size) => { sizes.current[n] = size; }, []);
  const update = (id, patch) => { setFields((list) => list.map((f) => (f.id === id ? { ...f, ...patch } : f))); setDirty(true); };
  const remove = (id) => { setFields((list) => list.filter((f) => f.id !== id)); setSelected(null); setDirty(true); };
  const add = (type, party) => {
    const info = typeInfo({ type, party });
    const count = fields.filter((f) => f.page === page).length;
    const f = { id: newId(), type, party, page, x: 0.08, y: clamp(0.08 + count * 0.06, 0, 0.9), w: info.w, h: info.h, label: '', required: true };
    setFields((list) => [...list, f]);
    setSelected(f.id);
    setDirty(true);
  };

  // Pointer drags: move from the body, resize from the corner handle; all in page fractions
  const startDrag = (e, f, mode) => {
    e.stopPropagation();
    e.preventDefault();
    const size = sizes.current[f.page];
    if (!size) return;
    drag.current = { id: f.id, mode, startX: e.clientX, startY: e.clientY, orig: { ...f }, size };
    setSelected(f.id);
    const move = (ev) => {
      const d = drag.current;
      if (!d) return;
      const dx = (ev.clientX - d.startX) / d.size.width;
      const dy = (ev.clientY - d.startY) / d.size.height;
      if (d.mode === 'move') update(d.id, { x: clamp(d.orig.x + dx, 0, 1 - d.orig.w), y: clamp(d.orig.y + dy, 0, 1 - d.orig.h) });
      else update(d.id, { w: clamp(d.orig.w + dx, 0.03, 1 - d.orig.x), h: clamp(d.orig.h + dy, 0.01, 1 - d.orig.y) });
    };
    const up = () => { drag.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/careers-admin/contract-templates/${template.id}`, { title, fields }, { headers: getAuthHeader() });
      notify('Template saved', 'success');
      setDirty(false);
      onSaved(data.template);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sel = fields.find((f) => f.id === selected) || null;
  const pageCount = pdf?.numPages || template.pageCount || 1;

  return (
    <Dialog open fullScreen onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
        <TextField size="small" label="Template title" value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} sx={{ minWidth: 300 }} />
        <Typography variant="body2" color="text.secondary">{fields.length} field{fields.length === 1 ? '' : 's'} · {pageCount} page{pageCount === 1 ? '' : 's'}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving...' : 'Save template'}</Button>
        <IconButton onClick={() => { if (!dirty || window.confirm('Close without saving your changes?')) onClose(); }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', gap: 2, p: 2, bgcolor: '#f3f4f6' }}>
        <Box sx={{ width: 300, flex: 'none' }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Add to page {page}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Contractor fills in</Typography>
            <Stack spacing={0.75}>
              {APPLICANT_FIELDS.map((t) => <Button key={t.type} size="small" variant="outlined" onClick={() => add(t.type, 'applicant')} sx={{ justifyContent: 'flex-start', borderColor: t.color, color: t.color }} disabled={!pdf}>{t.label}</Button>)}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, mb: 0.5 }}>You fill in</Typography>
            <Stack spacing={0.75}>
              {COMPANY_FIELDS.map((t) => <Button key={t.type} size="small" variant="outlined" onClick={() => add(t.type, 'company')} sx={{ justifyContent: 'flex-start', borderColor: t.color, color: t.color }} disabled={!pdf}>{t.label}</Button>)}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Drag a box to move it; drag its bottom-right corner to resize. Click a page to pick where new fields go.</Typography>
            {pageCount > 1 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => <Chip key={n} size="small" label={`p.${n}`} color={page === n ? 'primary' : 'default'} onClick={() => { setPage(n); document.getElementById(`tpl-page-${n}`)?.scrollIntoView({ behavior: 'smooth' }); }} />)}
              </Stack>
            )}
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Selected field</Typography>
            {!sel ? <Typography variant="body2" color="text.secondary">Click a box on the document.</Typography> : (
              <Stack spacing={1}>
                <Chip size="small" label={`${partyLabel(sel)}: ${typeInfo(sel).label} · page ${sel.page}`} sx={{ bgcolor: typeInfo(sel).color, color: '#fff', alignSelf: 'flex-start' }} />
                <TextField size="small" label={sel.party === 'company' && sel.type === 'text' ? 'What this blank is (shown to you when sending)' : 'Label (shown to the hire)'} value={sel.label || ''} onChange={(e) => update(sel.id, { label: e.target.value })} inputProps={{ maxLength: 80 }} placeholder={sel.type === 'text' ? (sel.party === 'company' ? 'e.g. Per-call fee' : 'e.g. Emergency contact') : ''} />
                {TYPED.includes(sel.type) && <FormControlLabel control={<Checkbox checked={sel.required !== false} onChange={(e) => update(sel.id, { required: e.target.checked })} />} label="Required" />}
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(sel.id)}>Remove field</Button>
              </Stack>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">Contractor side: signature and initials are drawn once and placed in every box of that kind; dates fill in on the day they sign; phone and email start from their application.</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Your side: blanks (fees, terms) are asked for when you send. Your signature, name and date are added when you press Sign off on their panel after they've signed, which produces the fully signed copy.</Typography>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {!pdf && !loadError && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
          {pdf && Array.from({ length: pdf.numPages }, (_, i) => i + 1).map((n) => (
            <Box key={n} id={`tpl-page-${n}`}>
              <Typography variant="caption" color="text.secondary">Page {n}</Typography>
              <PdfPage pdf={pdf} pageNumber={n} width={pageWidth} onSize={onSize} onPointerDown={() => { setPage(n); setSelected(null); }}>
                {fields.filter((f) => f.page === n).map((f) => {
                  const info = typeInfo(f);
                  const active = f.id === selected;
                  return (
                    <Box
                      key={f.id}
                      onPointerDown={(e) => startDrag(e, f, 'move')}
                      sx={{
                        position: 'absolute', left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                        border: `2px ${active ? 'solid' : 'dashed'} ${info.color}`, bgcolor: `${info.color}22`, cursor: 'move', boxSizing: 'border-box',
                        display: 'flex', alignItems: 'center', px: 0.5, overflow: 'hidden', userSelect: 'none', touchAction: 'none'
                      }}
                    >
                      <Typography variant="caption" sx={{ color: info.color, fontWeight: 700, whiteSpace: 'nowrap', fontSize: 11 }}>{f.party === 'company' ? '★ ' : ''}{f.label || info.label}</Typography>
                      <Box onPointerDown={(e) => startDrag(e, f, 'resize')} sx={{ position: 'absolute', right: -1, bottom: -1, width: 12, height: 12, bgcolor: info.color, cursor: 'nwse-resize' }} />
                    </Box>
                  );
                })}
              </PdfPage>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function ContractTemplates() {
  const [templates, setTemplates] = useState(null);
  const [wordSupported, setWordSupported] = useState(true);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [snack, setSnack] = useState(null);
  const input = useRef(null);
  const notify = useCallback((message, severity = 'info') => setSnack({ message, severity }), []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/careers-admin/contract-templates`, { headers: getAuthHeader() });
      setTemplates(data.templates || []);
      setWordSupported(data.wordSupported !== false);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load templates', 'error');
      setTemplates([]);
    }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('title', title);
      form.append('file', file);
      const { data } = await axios.post(`${API}/api/careers-admin/contract-templates`, form, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setTitle(''); setFile(null); if (input.current) input.current.value = '';
      await load();
      notify('Uploaded — now place the fields', 'success');
      setEditing(data.template);
    } catch (err) {
      notify(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (t) => {
    if (!window.confirm(`Remove "${t.title}" from the list? Contracts already sent are not affected.`)) return;
    try {
      await axios.delete(`${API}/api/careers-admin/contract-templates/${t.id}`, { headers: getAuthHeader() });
      await load();
      notify('Template removed', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to remove', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, mt: 2 }}>
        <Typography variant="h5">Contract templates</Typography>
        <Typography variant="body2" color="text.secondary">Upload a contract once, mark where a hire signs, initials, dates or types — and where you sign off and which blanks you fill in — then send it to any hire from their applicant panel. ★ marks your boxes.</Typography>
      </Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Add a template</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="Title" placeholder="e.g. Independent contractor agreement" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ minWidth: 280 }} />
          <Button variant="outlined" component="label" disabled={busy}>
            {file ? file.name : 'Choose PDF or Word file'}
            <input ref={input} hidden type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '')); }} />
          </Button>
          <Button variant="contained" disabled={busy || !file} onClick={upload}>{busy ? 'Uploading...' : 'Upload and place fields'}</Button>
        </Box>
        <Typography variant="caption" color="text.secondary">{wordSupported ? 'Word files are converted to PDF on the server.' : 'Word files: the server cannot convert them yet — in Word use File → Save As → PDF.'}</Typography>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Your templates</Typography>
        {templates === null && <CircularProgress size={20} />}
        {templates && templates.length === 0 && <Typography variant="body2" color="text.secondary">None yet. Upload one above.</Typography>}
        {templates && templates.length > 0 && (
          <Table size="small">
            <TableHead><TableRow><TableCell>Title</TableCell><TableCell>Pages</TableCell><TableCell>Fields</TableCell><TableCell>Updated</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.title}<Typography variant="caption" display="block" color="text.secondary">{t.originalName}</Typography></TableCell>
                  <TableCell>{t.pageCount}</TableCell>
                  <TableCell>
                    {t.fields.length === 0 ? <Chip size="small" color="warning" label="No fields yet" /> : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {ALL_FIELDS.map((ft) => { const n = t.fields.filter((f) => f.type === ft.type && (f.party || 'applicant') === ft.party).length; return n ? <Chip key={`${ft.party}-${ft.type}`} size="small" label={`${n} ${ft.party === 'company' ? '★ ' : ''}${ft.label.toLowerCase()}`} sx={{ bgcolor: ft.color, color: '#fff' }} /> : null; })}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption">{fmtEmailTime(t.updatedAt)}{t.updatedBy ? ` · ${t.updatedBy}` : ''}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Place fields"><IconButton size="small" onClick={() => setEditing(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => archive(t)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
      {editing && <TemplateEditor template={editing} notify={notify} onClose={() => setEditing(null)} onSaved={(t) => { setEditing(t); load(); }} />}
      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : undefined}
      </Snackbar>
      <DialogActions sx={{ display: 'none' }} />
    </Box>
  );
}

export default ContractTemplates;
